import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const AUTO_MS = 5500;

export default function LiteScreenshotCarousel({ slides }) {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  const goTo = useCallback(
    (next) => {
      if (!count) return;
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  const goPrev = () => goTo(index - 1);
  const goNext = () => goTo(index + 1);

  useEffect(() => {
    if (count <= 1) return undefined;
    const id = setInterval(() => goTo(index + 1), AUTO_MS);
    return () => clearInterval(id);
  }, [index, count, goTo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goTo(index - 1);
      if (e.key === 'ArrowRight') goTo(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, index]);

  if (!count) return null;

  const slide = slides[index];

  return (
    <div className="relative max-w-4xl mx-auto">
      <div className="rounded-2xl overflow-hidden border border-stone-200/80 bg-white shadow-xl shadow-indigo-500/5">
        <div className="relative bg-stone-100 min-h-[200px] sm:min-h-[280px]">
          <AnimatePresence mode="wait" initial={false}>
            <motion.img
              key={slide.src}
              src={slide.src}
              alt={slide.title}
              className="w-full h-auto block"
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            />
          </AnimatePresence>

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/95 border border-stone-200 shadow-md text-stone-700 hover:bg-white hover:text-indigo-700 flex items-center justify-center transition-colors"
                aria-label="Previous screenshot"
              >
                <span className="text-xl leading-none" aria-hidden>
                  ‹
                </span>
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/95 border border-stone-200 shadow-md text-stone-700 hover:bg-white hover:text-indigo-700 flex items-center justify-center transition-colors"
                aria-label="Next screenshot"
              >
                <span className="text-xl leading-none" aria-hidden>
                  ›
                </span>
              </button>
            </>
          )}
        </div>

        <figcaption className="px-5 sm:px-6 py-4 sm:py-5 border-t border-stone-100 bg-white">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={slide.title}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
            >
              <p className="font-semibold text-stone-900 text-base sm:text-lg">{slide.title}</p>
              <p className="text-sm text-stone-500 mt-1">{slide.caption}</p>
            </motion.div>
          </AnimatePresence>
        </figcaption>
      </div>

      {count > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
          {slides.map((s, i) => (
            <button
              key={s.title}
              type="button"
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? 'w-8 bg-indigo-600' : 'w-2 bg-stone-300 hover:bg-stone-400'
              }`}
              aria-label={`Go to slide ${i + 1}: ${s.title}`}
              aria-current={i === index ? 'true' : undefined}
            />
          ))}
        </div>
      )}

      {count > 1 && (
        <p className="text-center text-xs text-stone-400 mt-3">
          {index + 1} / {count} · Use arrows or dots to browse
        </p>
      )}
    </div>
  );
}
